import axios from "axios";

export const getBaseApiUrl = () => {
  if (typeof window !== "undefined") {
    const esLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    // En desarrollo local ignoramos config.js (apunta al backend publicado en IIS)
    // y usamos REACT_APP_BACKEND_URL para pegarle al backend que corre en esta máquina.
    if (!esLocal) {
      // 1. Configuración dinámica parametrizable en runtime (desde config.js o inyección)
      if (window.__ENV__ && window.__ENV__.API_URL) {
        return window.__ENV__.API_URL;
      }
      if (window.__ENV__ && window.__ENV__.BACKEND_URL) {
        return `${window.__ENV__.BACKEND_URL.replace(/\/+$/, "")}/api`;
      }
      // 2. Entorno de producción remoto: IIS reescribe /api hacia el backend
      return "/api";
    }
  }
  const backend = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
  return `${backend.replace(/\/+$/, "")}/api`;
};

export const API = getBaseApiUrl();

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Interceptor to handle deactivated/blocked accounts immediately
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      const detail = error.response.data?.detail || "";
      if (
        typeof detail === "string" &&
        (detail.toLowerCase().includes("bloqueada") ||
          detail.toLowerCase().includes("inactivo") ||
          detail.toLowerCase().includes("desactivada"))
      ) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login?blocked=true";
        }
      }
    }
    return Promise.reject(error);
  }
);

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const api = {
  // Auth
  login: (dpi, fechaNacimiento) =>
    axios.post(`${API}/auth/login`, { dpi, fecha_nacimiento: fechaNacimiento }),
  
  getMe: () =>
    axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),

  // Employees
  getEmployees: () =>
    axios.get(`${API}/empleados`, { headers: getAuthHeader() }),
  
  getEmployee: (id) =>
    axios.get(`${API}/empleados/${id}`, { headers: getAuthHeader() }),
  
  createEmployee: (data) =>
    axios.post(`${API}/empleados`, data, { headers: getAuthHeader() }),
  
  updateEmployee: (id, data) =>
    axios.put(`${API}/empleados/${id}`, data, { headers: getAuthHeader() }),
  
  deleteEmployee: (id) =>
    axios.delete(`${API}/empleados/${id}`, { headers: getAuthHeader() }),
  
  importEmployees: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios.post(`${API}/empleados/importar-csv`, formData, {
      headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
    });
  },

  getSupervisors: () =>
    axios.get(`${API}/supervisores`, { headers: getAuthHeader() }),

  recalculateBalances: () =>
    axios.post(`${API}/empleados/recalcular`, {}, { headers: getAuthHeader() }),
  getTeamStatistics: (tipo = "directos") =>
    axios.get(`${API}/supervisor/equipo`, { 
      params: { tipo },
      headers: getAuthHeader() 
    }),

  // Vacation Requests
  getVacationRequests: () =>
    axios.get(`${API}/vacaciones`, { headers: getAuthHeader() }),
  
  getPendingRequests: () =>
    axios.get(`${API}/vacaciones/pendientes`, { headers: getAuthHeader() }),
  
  getVacationRequest: (id) =>
    axios.get(`${API}/vacaciones/${id}`, { headers: getAuthHeader() }),
  
  createVacationRequest: (data) =>
    axios.post(`${API}/vacaciones`, data, { headers: getAuthHeader() }),
  
  approveRequest: (id, comentario) =>
    axios.post(`${API}/vacaciones/${id}/aprobar`, { comentario }, { headers: getAuthHeader() }),
  
  rejectRequest: (id, comentario) =>
    axios.post(`${API}/vacaciones/${id}/rechazar`, { comentario }, { headers: getAuthHeader() }),

  cancelRequest: (id, comentario) =>
    axios.post(`${API}/vacaciones/${id}/anular`, { comentario }, { headers: getAuthHeader() }),

  // Calendar
  getCalendarEvents: (mes, año) =>
    axios.get(`${API}/calendario`, {
      params: { mes, año },
      headers: getAuthHeader(),
    }),

  // Admin & Supervisor Bulk Actions
  bulkUpdateSupervisor: (employeeIds, supervisorId) => 
    axios.post(`${API}/admin/bulk-update-supervisor`, { employee_ids: employeeIds, supervisor_id: supervisorId }, { headers: getAuthHeader() }),

  // Employee Details & History
  getEmployeeDetails: (id) => 
    axios.get(`${API}/empleados/${id}/detalles`, { headers: getAuthHeader() }),

  // Stats
  getStatistics: () =>
    axios.get(`${API}/estadisticas`, { headers: getAuthHeader() }),

  // Reports
  getEmployeeReport: (params) =>
    axios.get(`${API}/admin/reporte-empleados`, { params, headers: getAuthHeader() }),

  manualAdjustment: (id, data) =>
    axios.post(`${API}/empleados/${id}/ajuste-manual`, data, { headers: getAuthHeader() }),

  // Holidays
  getHolidays: (pais) =>
    axios.get(`${API}/feriados`, { params: { pais }, headers: getAuthHeader() }),
  createHoliday: (data) =>
    axios.post(`${API}/feriados`, data, { headers: getAuthHeader() }),
  deleteHoliday: (id) =>
    axios.delete(`${API}/feriados/${id}`, { headers: getAuthHeader() }),
  updateHoliday: (id, data) =>
    axios.put(`${API}/feriados/${id}`, data, { headers: getAuthHeader() }),
  preloadHolidays: (pais, anio) =>
    axios.post(`${API}/feriados/precargar`, null, { params: { pais, anio }, headers: getAuthHeader() }),
  downloadVacationSlip: (id) =>
    axios.get(`${API}/vacaciones/${id}/boleta`, { responseType: 'blob', headers: getAuthHeader() }),

  // Constancias
  getMisConstancias: () =>
    axios.get(`${API}/constancias/mis-solicitudes`, { headers: getAuthHeader() }),
  getAllConstancias: (estado) =>
    axios.get(`${API}/constancias`, { params: estado ? { estado } : {}, headers: getAuthHeader() }),
  createConstancia: (data) =>
    axios.post(`${API}/constancias`, data, { headers: getAuthHeader() }),
  responderConstancia: (id, formData) =>
    axios.post(`${API}/constancias/${id}/responder`, formData, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }),
  downloadConstancia: (id) =>
    axios.get(`${API}/constancias/${id}/descargar`, { responseType: 'blob', headers: getAuthHeader() }),

  // Llamadas de Atención
  getLlamadas: (empleadoId) =>
    axios.get(`${API}/llamadas-atencion`, { params: empleadoId ? { empleado_id: empleadoId } : {}, headers: getAuthHeader() }),
  createLlamada: (formData) =>
    axios.post(`${API}/llamadas-atencion`, formData, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }),
  downloadLlamadaAdjunto: (id) =>
    axios.get(`${API}/llamadas-atencion/${id}/descargar`, { responseType: 'blob', headers: getAuthHeader() }),
  downloadActaPdf: (id) =>
    axios.get(`${API}/llamadas-atencion/${id}/acta-pdf`, { responseType: 'blob', headers: getAuthHeader() }),

  // Boletos de Ornato
  getMisBoletos: () =>
    axios.get(`${API}/boletos-ornato/mis-boletos`, { headers: getAuthHeader() }),
  getAllBoletos: (anio) =>
    axios.get(`${API}/boletos-ornato`, { params: anio ? { anio } : {}, headers: getAuthHeader() }),
  uploadBoletosMasivo: (formData) =>
    axios.post(`${API}/boletos-ornato/carga-masiva`, formData, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }),
  uploadBoletoIndividual: (formData) =>
    axios.post(`${API}/boletos-ornato/individual`, formData, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }),
  downloadBoleto: (id) =>
    axios.get(`${API}/boletos-ornato/${id}/descargar`, { responseType: 'blob', headers: getAuthHeader() }),
  deleteBoleto: (id) =>
    axios.delete(`${API}/boletos-ornato/${id}`, { headers: getAuthHeader() }),

  // Manuales y Gamificación
  getManuales: (categoria) =>
    axios.get(`${API}/manuales`, { params: categoria ? { categoria } : {}, headers: getAuthHeader() }),
  getMiProgresoManuales: () =>
    axios.get(`${API}/manuales/mi-progreso`, { headers: getAuthHeader() }),
  completarManual: (id) =>
    axios.post(`${API}/manuales/${id}/completar`, {}, { headers: getAuthHeader() }),
  getMetricasManuales: () =>
    axios.get(`${API}/manuales/metricas`, { headers: getAuthHeader() }),
  createManual: (formData) =>
    axios.post(`${API}/manuales`, formData, { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }),
  downloadManual: (id) =>
    axios.get(`${API}/manuales/${id}/descargar`, { responseType: 'blob', headers: getAuthHeader() }),
  deleteManual: (id) =>
    axios.delete(`${API}/manuales/${id}`, { headers: getAuthHeader() }),

  // Marcaje y Asistencia (CrossChex Cloud)
  getEmpresasMarcaje: () =>
    axios.get(`${API}/marcaje/empresas`, { headers: getAuthHeader() }),
  createEmpresaMarcaje: (data) =>
    axios.post(`${API}/marcaje/empresas`, data, { headers: getAuthHeader() }),
  updateEmpresaMarcaje: (id, data) =>
    axios.put(`${API}/marcaje/empresas/${id}`, data, { headers: getAuthHeader() }),
  deleteEmpresaMarcaje: (id) =>
    axios.delete(`${API}/marcaje/empresas/${id}`, { headers: getAuthHeader() }),
  testEmpresaMarcaje: (id) =>
    axios.post(`${API}/marcaje/empresas/${id}/test`, {}, { headers: getAuthHeader() }),
  getDepartamentosMarcaje: (empresaId) =>
    axios.get(`${API}/marcaje/empresas/${empresaId}/departamentos`, { headers: getAuthHeader() }),
  getPuestosMarcaje: (empresaId) =>
    axios.get(`${API}/marcaje/empresas/${empresaId}/puestos`, { headers: getAuthHeader() }),
  sincronizarDepartamentosMarcaje: (empresaId, data) =>
    axios.post(`${API}/marcaje/empresas/${empresaId}/departamentos/sincronizar`, data, { headers: getAuthHeader() }),
  deleteDepartamentoMarcaje: (deptId) =>
    axios.delete(`${API}/marcaje/departamentos/${deptId}`, { headers: getAuthHeader() }),
  iniciarGeneracionMarcaje: (data) =>
    axios.post(`${API}/marcaje/iniciar-generacion`, data, { headers: getAuthHeader() }),
  getEstadoTrabajoMarcaje: (jobId) =>
    axios.get(`${API}/marcaje/trabajos/${jobId}/estado`, { headers: getAuthHeader() }),
  descargarTrabajoMarcaje: (jobId) =>
    axios.get(`${API}/marcaje/trabajos/${jobId}/descargar`, { responseType: 'blob', headers: getAuthHeader() }),
  generarReporteMarcaje: (data) =>
    axios.post(`${API}/marcaje/generar`, data, { responseType: 'blob', headers: getAuthHeader() }),

  // Notificaciones & Push
  getNotificaciones: () =>
    axios.get(`${API}/notificaciones`, { headers: getAuthHeader() }),
  getUnreadCount: () =>
    axios.get(`${API}/notificaciones/conteo-no-leidas`, { headers: getAuthHeader() }),
  marcarNotificacionLeida: (id) =>
    axios.put(`${API}/notificaciones/${id}/leer`, {}, { headers: getAuthHeader() }),
  marcarTodasLeidas: () =>
    axios.put(`${API}/notificaciones/marcar-todas-leidas`, {}, { headers: getAuthHeader() }),
  getVapidPublicKey: () =>
    axios.get(`${API}/notificaciones/vapid-public-key`),
  subscribePush: (data) =>
    axios.post(`${API}/notificaciones/push-subscripcion`, data, { headers: getAuthHeader() }),
  unsubscribePush: (data) =>
    axios.post(`${API}/notificaciones/push-desubscripcion`, data, { headers: getAuthHeader() }),
  sendBroadcast: (data) => {
    const isFormData = data instanceof FormData;
    return axios.post(`${API}/notificaciones/comunicado-rrhh`, data, {
      headers: {
        ...getAuthHeader(),
        ...(isFormData ? { "Content-Type": "multipart/form-data" } : {})
      }
    });
  },
  getBroadcasts: () =>
    axios.get(`${API}/notificaciones/comunicados-historial`, { headers: getAuthHeader() }),
  getBroadcastLogs: () =>
    axios.get(`${API}/notificaciones/comunicados-logs`, { headers: getAuthHeader() }),
  downloadBroadcastAdjunto: (id) =>
    axios.get(`${API}/notificaciones/comunicados/${id}/adjunto`, { responseType: 'blob', headers: getAuthHeader() }),
  triggerBroadcastsScheduled: () =>
    axios.post(`${API}/notificaciones/comunicados-procesar-programados`, {}, { headers: getAuthHeader() }),
  sendBroadcastNow: (id) =>
    axios.post(`${API}/notificaciones/comunicados/${id}/enviar-ahora`, {}, { headers: getAuthHeader() }),
  cancelBroadcastSchedule: (id) =>
    axios.put(`${API}/notificaciones/comunicados/${id}/cancelar-programacion`, {}, { headers: getAuthHeader() }),

  // Beneficios CPS Corazón Naranja
  getBeneficios: () =>
    axios.get(`${API}/beneficios`, { headers: getAuthHeader() }),
  createBeneficio: (data) =>
    axios.post(`${API}/beneficios`, data, { headers: getAuthHeader() }),
  updateBeneficio: (id, data) =>
    axios.put(`${API}/beneficios/${id}`, data, { headers: getAuthHeader() }),
  deleteBeneficio: (id) =>
    axios.delete(`${API}/beneficios/${id}`, { headers: getAuthHeader() }),

  // Seed
  seedDatabase: () => axios.post(`${API}/seed`),
};

export async function subscribeToPushNotifications() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Las notificaciones Push no son soportadas en este navegador o dispositivo.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permiso de notificaciones push denegado por el usuario.');
  }

  const registration = await navigator.serviceWorker.ready;
  const { data } = await api.getVapidPublicKey();
  if (!data?.publicKey) {
    throw new Error('No se pudo obtener la clave VAPID pública del servidor.');
  }

  const convertedVapidKey = urlBase64ToUint8Array(data.publicKey);
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: convertedVapidKey,
  });

  const subJson = subscription.toJSON();
  await api.subscribePush({
    endpoint: subJson.endpoint,
    p256dh: subJson.keys?.p256dh,
    auth: subJson.keys?.auth,
  });

  return subscription;
}

export default api;

